import { Component, type ErrorInfo, type ReactNode } from 'react'
import type { Locale } from '../content/site'

const COPY = {
  title: { en: 'The shell crashed', ru: 'Оболочка упала' },
  body: {
    en: 'Something threw during render. Reloading usually clears it; if not, local settings may be corrupt.',
    ru: 'Что-то упало во время отрисовки. Обычно помогает перезагрузка; если нет — возможно, повреждены локальные настройки.',
  },
  reload: { en: 'Reload', ru: 'Перезагрузить' },
  reset: { en: 'Reset local settings and reload', ru: 'Сбросить настройки и перезагрузить' },
} as const

type Props = { locale: Locale; children: ReactNode }
type State = { error: Error | null }

/**
 * Without this, any throw during render leaves `#root` empty and the visitor
 * sees a black page with no explanation and no way to recover.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack)
  }

  private reset = (): void => {
    try {
      localStorage.clear()
    } catch {
      // Nothing to clear when storage is unavailable.
    }
    location.reload()
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    const { locale } = this.props
    return (
      <main className="device-notice" lang={locale}>
        <div>
          <p className="device-notice__prompt" aria-hidden="true">
            !_
          </p>
          <h1>{COPY.title[locale]}</h1>
          <p>{COPY.body[locale]}</p>
          <p className="device-notice__actions">
            <button type="button" onClick={() => location.reload()}>
              {COPY.reload[locale]}
            </button>
            <button type="button" onClick={this.reset}>
              {COPY.reset[locale]}
            </button>
          </p>
        </div>
      </main>
    )
  }
}
